import * as React from "react";
import clsx from "clsx";
import { Shield } from "lucide-react";
import type {
  EffectiveProductAccess,
  ProductAccessTier,
  ProductAccessTierOrCustom,
} from "../../lib/api/product-access";
import { PRODUCT_ACCESS_TIER_OPTIONS } from "../../lib/api/product-access";
import * as s from "./ProductPage.css";

function tierChipClass(tier: ProductAccessTierOrCustom | null | undefined): string {
  switch (tier) {
    case "full":
      return s.tierChipFull;
    case "edit":
      return s.tierChipEdit;
    case "view":
      return s.tierChipView;
    case "custom":
      return s.tierChipCustom;
    default:
      return "";
  }
}

export function formatProductAccessTooltip(
  access: EffectiveProductAccess | null | undefined,
): string {
  if (!access?.tier) return "You do not have access to this product";
  const label =
    PRODUCT_ACCESS_TIER_OPTIONS.find((o) => o.value === access.tier)?.label.toLowerCase() ??
    String(access.tier);
  const source = access.sources?.[0];
  if (!source) return `You have ${label}`;

  if (source.kind === "super-admin") {
    return `You have ${label} — super admin`;
  }
  if (source.kind === "direct") {
    return `You have ${label} — shared directly on this product`;
  }
  if (source.kind === "inherited") {
    const role =
      source.roleLabel?.trim() ||
      source.roleName?.trim() ||
      "organization role";
    const org = source.organizationName?.trim() || "your organization";
    return `You have ${label} — inherited from your ${role} role at ${org}`;
  }
  return source.label || `You have ${label}`;
}

export function productAccessTierLabel(
  tier: ProductAccessTier | ProductAccessTierOrCustom | null | undefined,
): string {
  return (
    PRODUCT_ACCESS_TIER_OPTIONS.find((o) => o.value === tier)?.label ??
    (tier === "custom" ? "Custom" : "—")
  );
}

export function ProductAccessTierBadge({
  tier,
  title,
  onClick,
  showShield = false,
  endAdornment,
}: {
  tier: ProductAccessTier | ProductAccessTierOrCustom | null;
  title?: string;
  onClick?: () => void;
  /** Shield icon indicating access source (inherited / shared). */
  showShield?: boolean;
  endAdornment?: React.ReactNode;
}) {
  const label = productAccessTierLabel(tier);
  const colorClass = tierChipClass(tier);
  const content = (
    <>
      {showShield ? <Shield size={11} aria-hidden /> : null}
      {label}
      {endAdornment}
    </>
  );

  if (!onClick) {
    return (
      <span className={clsx(s.tierChip, colorClass)} title={title}>
        {content}
      </span>
    );
  }

  return (
    <button
      type="button"
      title={title}
      aria-label={`Access level: ${label}. Click to request a different level.`}
      onClick={onClick}
      className={clsx(s.tierChipButton, colorClass)}
    >
      {content}
    </button>
  );
}
